import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ComposeDialog } from '@/components/email/ComposeDialog';
import { EmailDashboard } from '@/components/email/EmailDashboard';
import { ClientIssuesTable } from '@/components/email/ClientIssuesTable';
import { CampaignList } from '@/components/email/CampaignList';
import { EmailQueueTable } from '@/components/email/EmailQueueTable';
import { EmailDeliveriesTable } from '@/components/email/EmailDeliveriesTable';
import { EmailTemplatesPanel } from '@/components/email/EmailTemplatesPanel';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { Mail } from 'lucide-react';

export default function EmailStudio() {
  const [searchParams] = useSearchParams();
  const { can } = useAuth();
  const canBroadcast = can('email.broadcast');

  const toUserId = searchParams.get('to');
  const toName = searchParams.get('name');
  const toEmail = searchParams.get('email');
  const programId = searchParams.get('program');
  const programName = searchParams.get('programName');
  const draft = searchParams.get('draft');

  const hasComposeParams = toUserId || programId || draft;
  const defaultTab = hasComposeParams || !canBroadcast ? 'compose' : 'dashboard';

  return (
    <div className="email-studio space-y-6">
      <div className="flex items-center gap-3">
        <Mail className="h-6 w-6" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Email Studio</h1>
          <p className="text-sm text-muted-foreground">
            {canBroadcast
              ? 'Send personalised emails, manage campaigns, and monitor client issues.'
              : 'Send personalised emails to the users assigned to you.'}
          </p>
        </div>
      </div>

      <Tabs defaultValue={defaultTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="compose">Compose</TabsTrigger>
          {canBroadcast && <TabsTrigger value="dashboard">Dashboard</TabsTrigger>}
          {canBroadcast && <TabsTrigger value="queue">Queue</TabsTrigger>}
          {canBroadcast && <TabsTrigger value="deliveries">Deliveries</TabsTrigger>}
          <TabsTrigger value="templates">Templates</TabsTrigger>
          {canBroadcast && <TabsTrigger value="issues">Client Issues</TabsTrigger>}
          {canBroadcast && <TabsTrigger value="campaigns">Campaigns</TabsTrigger>}
        </TabsList>

        <TabsContent value="compose" className="space-y-4">
          <ComposeDialog
            prefilledUserId={toUserId}
            prefilledName={toName}
            prefilledEmail={toEmail}
            prefilledProgramId={programId}
            prefilledProgramName={programName}
            prefilledDraft={draft}
          />
        </TabsContent>

        {canBroadcast && (
          <>
            <TabsContent value="dashboard" className="space-y-4">
              <EmailDashboard />
            </TabsContent>

            <TabsContent value="queue" className="space-y-4">
              <EmailQueueTable />
            </TabsContent>

            <TabsContent value="deliveries" className="space-y-4">
              <EmailDeliveriesTable />
            </TabsContent>
          </>
        )}

        <TabsContent value="templates" className="space-y-4">
          <EmailTemplatesPanel />
        </TabsContent>

        {canBroadcast && (
          <>
            <TabsContent value="issues" className="space-y-4">
              <ClientIssuesTable />
            </TabsContent>

            <TabsContent value="campaigns" className="space-y-4">
              <CampaignList />
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}
